if (!customElements.get('bundle-items-card')) {
  const bundleCardCache = new Map();
  let bundleCardQueue = new Map();
  let bundleCardFlushTimer = null;
  const BATCH_CHUNK_SIZE = 50;

  const BATCH_QUERY = `
    query BundleComponentsBatch($ids: [ID!]!) {
      nodes(ids: $ids) {
        ... on Product {
          id
          variants(first: 10) {
            nodes {
              id
              requiresComponents
              components(first: 25) {
                nodes {
                  quantity
                  productVariant {
                    image { url altText }
                    product { handle title }
                  }
                }
              }
            }
          }
        }
      }
    }
  `;

  function getStorefrontAccessToken() {
    const metaTag = document.querySelector('meta[name="shopify-storefront-token"]');
    if (metaTag?.content) return metaTag.content;
    if (window.Shopify?.storefrontAccessToken) return window.Shopify.storefrontAccessToken;
    return null;
  }

  function getStorefrontHeaders() {
    const headers = { 'Content-Type': 'application/json' };
    const token = getStorefrontAccessToken();
    if (token) headers['X-Shopify-Storefront-Access-Token'] = token;
    return headers;
  }

  function enqueueBundleCard(element) {
    const productGid = element.dataset.productGid;
    if (bundleCardCache.has(productGid)) {
      element.renderFromVariants(bundleCardCache.get(productGid));
      return;
    }
    if (!bundleCardQueue.has(productGid)) bundleCardQueue.set(productGid, []);
    bundleCardQueue.get(productGid).push(element);
    if (!bundleCardFlushTimer) bundleCardFlushTimer = setTimeout(flushBundleCardQueue, 0);
  }

  async function flushBundleCardQueue() {
    bundleCardFlushTimer = null;
    const batch = bundleCardQueue;
    bundleCardQueue = new Map();
    const ids = [...batch.keys()];
    if (!ids.length) return;
    const shopDomain = batch.get(ids[0])[0].dataset.shopDomain;

    for (let i = 0; i < ids.length; i += BATCH_CHUNK_SIZE) {
      const chunk = ids.slice(i, i + BATCH_CHUNK_SIZE);
      try {
        const response = await fetch(`https://${shopDomain}/api/2025-04/graphql.json`, {
          method: 'POST',
          headers: getStorefrontHeaders(),
          body: JSON.stringify({ query: BATCH_QUERY, variables: { ids: chunk } }),
        });
        if (response.ok) {
          const json = await response.json();
          for (const node of json?.data?.nodes ?? []) {
            if (node?.id) bundleCardCache.set(node.id, node.variants?.nodes ?? null);
          }
        }
      } catch {
        // Silently fail — cards stay hidden for products we couldn't resolve
      }
      for (const productGid of chunk) {
        if (!bundleCardCache.has(productGid)) bundleCardCache.set(productGid, null);
        const variants = bundleCardCache.get(productGid);
        for (const element of batch.get(productGid) ?? []) {
          element.renderFromVariants(variants);
        }
      }
    }
  }

  customElements.define(
    'bundle-items-card',
    class BundleItemsCard extends HTMLElement {
      connectedCallback() {
        if (!this.dataset.productGid || !this.dataset.shopDomain) return;
        enqueueBundleCard(this);
      }

      renderFromVariants(variants) {
        const bundleVariant = variants?.find(
          (variant) => variant.requiresComponents && variant.components?.nodes?.length
        );
        if (!bundleVariant) return;
        const items = this.aggregateComponents(bundleVariant.components.nodes);
        if (!items.length) return;

        if (this.dataset.display === 'text') {
          this.renderText(items);
        } else {
          this.renderList(items);
        }
        this.removeAttribute('hidden');
      }

      aggregateComponents(components) {
        const seen = new Map();
        for (const component of components) {
          const handle = component.productVariant?.product?.handle;
          if (!handle) continue;
          if (seen.has(handle)) {
            seen.get(handle).quantity += component.quantity;
          } else {
            seen.set(handle, {
              title: component.productVariant.product.title,
              imageUrl: component.productVariant.image?.url ?? '',
              imageAlt: component.productVariant.image?.altText ?? '',
              quantity: component.quantity,
            });
          }
        }
        const items = [...seen.values()];
        if (this.dataset.sortByQuantity === 'true') {
          items.sort((a, b) => b.quantity - a.quantity);
        }
        return items;
      }

      renderText(items) {
        const parts = items
          .map((item) => `${item.quantity} ${this.escapeHtml(item.title)}`)
          .join(', ');
        this.innerHTML = `<p class="bundle-items-card__text">Includes: ${parts}</p>`;
      }

      renderList(items) {
        if (this.closest('.collection-plp-group')) {
          this.renderGroupList(items);
          return;
        }

        const heading = this.dataset.heading || "What's Included";
        const rows = items
          .map((item) => {
            let imageHtml = '';
            if (item.imageUrl) {
              const imageSeparator = item.imageUrl.includes('?') ? '&' : '?';
              const src = `${item.imageUrl}${imageSeparator}width=96`;
              const stackCount = Math.min(item.quantity, 4);
              const images = Array.from(
                { length: stackCount },
                () => `<img
                  src="${src}"
                  alt=""
                  class="bundle-items-card__image"
                  loading="lazy"
                  width="24"
                  height="44"
                >`
              ).join('');
              imageHtml = `<span class="bundle-items-card__stack" role="img" aria-label="${this.escapeHtml(
                item.imageAlt || item.title
              )}">${images}</span>`;
            }
            return `<li class="bundle-items-card__item">
              ${imageHtml}
              <span class="bundle-items-card__item-text">${item.quantity} ${this.escapeHtml(item.title)}</span>
            </li>`;
          })
          .join('');
        this.innerHTML = `
          <p class="bundle-items-card__heading">${this.escapeHtml(heading)}</p>
          <ul class="bundle-items-card__list" aria-label="${this.escapeHtml(heading)}">${rows}</ul>
        `;
      }

      renderGroupList(items) {
        const heading = this.dataset.heading || "What's Included";
        const count = items.length;
        const countClass =
          count <= 2
            ? 'collection-plp-group-card__included-grid--count-2'
            : count === 3
              ? 'collection-plp-group-card__included-grid--count-3'
              : 'collection-plp-group-card__included-grid--count-4';
        const rows = items
          .map((item) => {
            const label = `${item.quantity} ${item.title}`;
            let imageHtml = '';
            if (item.imageUrl) {
              const imageSeparator = item.imageUrl.includes('?') ? '&' : '?';
              const src = `${item.imageUrl}${imageSeparator}width=240`;
              const stackCount = Math.min(item.quantity, 4);
              if (stackCount > 1) {
                const stackClass =
                  stackCount === 2
                    ? 'collection-plp-included-stack collection-plp-included-stack--duo'
                    : 'collection-plp-included-stack';
                const stackItems = Array.from(
                  { length: stackCount },
                  () =>
                    `<div class="collection-plp-included-stack__item"><div class="collection-plp-included-media"><img src="${src}" alt="" class="collection-plp-included-image" loading="lazy" width="19" height="61"></div></div>`
                ).join('');
                imageHtml = `<div class="${stackClass}" role="img" aria-label="${this.escapeHtml(label)}">${stackItems}</div>`;
              } else {
                imageHtml = `<div class="collection-plp-included-media"><img src="${src}" alt="${this.escapeHtml(
                  item.imageAlt || item.title
                )}" class="collection-plp-included-image" loading="lazy" width="19" height="61"></div>`;
              }
            }
            const mediaSlot = imageHtml
              ? `<div class="collection-plp-group-card__included-media-slot">${imageHtml}</div>`
              : '';
            return `<div class="collection-plp-group-card__included-item">${mediaSlot}<p class="collection-plp-group-card__included-label">${this.escapeHtml(label)}</p></div>`;
          })
          .join('');

        this.innerHTML = `
          <hr class="collection-plp-group-card__included-divider" aria-hidden="true">
          <p class="collection-plp-group-card__included-heading">${this.escapeHtml(heading)}</p>
          <div class="collection-plp-group-card__included-grid ${countClass}" data-included-count="${count}">${rows}</div>
        `;
      }

      escapeHtml(str) {
        const div = document.createElement('div');
        div.textContent = str;
        return div.innerHTML;
      }
    }
  );
}
